import {
	OVERVIEW_CARD_HEIGHT,
	OVERVIEW_CARD_WIDTH,
	OVERVIEW_GRID_COLUMNS,
	OVERVIEW_GRID_GAP_X,
	OVERVIEW_GRID_GAP_Y,
	OVERVIEW_ORIGIN_X,
	OVERVIEW_ORIGIN_Y,
} from "./hierarchyDisclosure";

/**
 * CTX-173: geometry-driven initial camera, replacing a fixed `minZoom`
 * backstop (`ccc7cc54`'s rejected fix). A card is only as readable as its
 * on-screen pixel size — the same `OVERVIEW_CARD_WIDTH` compact card can be a
 * legible ~160px box on a 1440px-wide laptop viewport, or an illegible
 * ~15px speck once `fitView` has to frame a whole-account ELK sprawl — no
 * fixed zoom floor is right for every screen and every account size. This
 * module ties both ends of that trade-off to the *actual measured* canvas
 * viewport in pixels and a minimum-legible-card-size constant:
 *
 *  - how many compact cards the overview grid shows at once
 *    (`computeAdaptiveOverviewCapacity`) — so the grid itself is built to
 *    already fit at a readable scale, instead of relying on `fitView`'s
 *    `minZoom` clamp to rescue an oversized grid after the fact (which only
 *    prevents the zoom from going *below* the floor — it does nothing to
 *    stop the fitted content from being clipped off-screen when the natural
 *    fit-zoom is even lower than that).
 *  - the safety-net floor passed to `fitView` itself
 *    (`computeReadableZoomFloor`), for the residual case (a single
 *    real container with more content than any capacity cap accounts for)
 *    where even the bounded overview doesn't fully fit — bounded pan/drill-down
 *    is still available, per the ticket's "show a bounded semantic overview
 *    instead of shrinking everything to fit."
 *
 * Every caller here that hasn't measured a real viewport yet (first paint,
 * or a unit test that never mocks `clientWidth`/`clientHeight`) must fall
 * back to the pre-existing fixed defaults (`DEFAULT_VISIBLE_VPC_LIMIT` etc.)
 * rather than calling into this module with a zero-sized viewport — see the
 * call site in `InsightTopologyCanvas.tsx`.
 */

export interface ViewportPixelSize {
	width: number;
	height: number;
}

/** Below this on-screen pixel width, a compact overview card's label/counts/badges stop being legible — every computation in this module is anchored to this floor. */
export const MIN_READABLE_CARD_PX = 150;

/** Mirrors the `padding` fraction this module's callers pass to `fitView` — kept in sync here so the capacity math and the actual `fitView` call agree on what "fits" means. */
export const OVERVIEW_FIT_PADDING = 0.15;

/** The zoom level at which a `cardWidth` (world-px) card renders at exactly `minCardPx` screen px. */
export function computeReadableZoomFloor(cardWidth: number = OVERVIEW_CARD_WIDTH, minCardPx: number = MIN_READABLE_CARD_PX): number {
	if (!(cardWidth > 0)) return 1;
	return minCardPx / cardWidth;
}

/** The minimal node shape the scoped-viewport math needs — matches ReactFlow's `Node` structurally so callers can pass their node arrays straight through. */
export interface ScopedViewportNode {
	id: string;
	parentId?: string;
	position: { x: number; y: number };
	width?: number | null;
	height?: number | null;
}

export interface ScopedViewportTarget {
	x: number;
	y: number;
	zoom: number;
}

export interface ScopedViewportOptions {
	/** Same convention as `fitView`'s `padding`: the fitted bounds are treated as `(1 + padding)` times their real size. */
	padding?: number;
	minZoom?: number;
	maxZoom?: number;
}

/**
 * Deterministic replacement for `fitView({ nodes: [{ id }] })` on a focus
 * change: computes the target viewport straight from the freshly laid-out
 * node array the caller is about to render, instead of asking ReactFlow's
 * store for the node's bounds. The store lookup raced the layout commit —
 * when the id resolved to a pre-layout position (or didn't resolve at all,
 * silently falling back to fit-everything) the camera framed coordinates
 * thousands of pixels from the actual content, leaving the canvas blank or
 * microscopic (observed live against the DISH staging digest, 2026-08-03).
 *
 * `scopeIds` semantics: `null`/empty frames every node (the overview);
 * otherwise frames the union of the matching nodes' absolute rects PLUS each
 * one's direct children — a focused VPC's gateway tier and attachment nodes
 * sit deliberately ABOVE its border, so the container's own box alone would
 * crop them.
 */
export function computeScopedViewport(
	nodes: readonly ScopedViewportNode[],
	scopeIds: string | readonly string[] | null,
	viewport: ViewportPixelSize,
	options: ScopedViewportOptions = {}
): ScopedViewportTarget | null {
	if (!(viewport.width > 0) || !(viewport.height > 0) || nodes.length === 0) return null;
	const padding = options.padding ?? 0;
	const minZoom = options.minZoom ?? 0;
	const maxZoom = options.maxZoom ?? Number.POSITIVE_INFINITY;

	const byId = new Map(nodes.map((node) => [node.id, node]));
	const absOrigin = (node: ScopedViewportNode): { x: number; y: number } => {
		let x = node.position.x;
		let y = node.position.y;
		let parent = node.parentId ? byId.get(node.parentId) : undefined;
		while (parent) {
			x += parent.position.x;
			y += parent.position.y;
			parent = parent.parentId ? byId.get(parent.parentId) : undefined;
		}
		return { x, y };
	};

	const wanted = scopeIds == null ? null : new Set(typeof scopeIds === "string" ? [scopeIds] : scopeIds);
	let scoped: ScopedViewportNode[];
	if (wanted && wanted.size > 0) {
		scoped = nodes.filter((node) => wanted.has(node.id) || (node.parentId != null && wanted.has(node.parentId)));
		// An id that doesn't resolve this frame must NOT silently widen to the
		// whole graph (fitView's fallback did, producing the zoomed-out-to-dust
		// frames) — the caller decides the fallback by passing `null` explicitly.
		if (!nodes.some((node) => wanted.has(node.id))) return null;
	} else {
		scoped = [...nodes];
	}
	if (scoped.length === 0) return null;

	let minX = Number.POSITIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;
	scoped.forEach((node) => {
		const { x, y } = absOrigin(node);
		const width = typeof node.width === "number" ? node.width : 0;
		const height = typeof node.height === "number" ? node.height : 0;
		minX = Math.min(minX, x);
		minY = Math.min(minY, y);
		maxX = Math.max(maxX, x + width);
		maxY = Math.max(maxY, y + height);
	});
	const boundsWidth = maxX - minX;
	const boundsHeight = maxY - minY;
	if (!(boundsWidth > 0) || !(boundsHeight > 0)) return null;

	// Same math as ReactFlow's `getViewportForBounds`, with the same
	// min/max clamp applied afterwards.
	const fitZoom = Math.min(viewport.width / (boundsWidth * (1 + padding)), viewport.height / (boundsHeight * (1 + padding)));
	const zoom = Math.min(Math.max(fitZoom, minZoom), maxZoom);
	const centerX = minX + boundsWidth / 2;
	const centerY = minY + boundsHeight / 2;
	return {
		x: viewport.width / 2 - centerX * zoom,
		y: viewport.height / 2 - centerY * zoom,
		zoom,
	};
}

export interface OverviewCapacityOptions {
	cardWidth?: number;
	cardHeight?: number;
	gapX?: number;
	gapY?: number;
	originX?: number;
	originY?: number;
	columnsHint?: number;
	minCardPx?: number;
	padding?: number;
	/** Never return fewer than this many visible slots — a handful of real cards is still more useful than an overly aggressive collapse on a very small viewport. */
	minVisibleCount?: number;
	/** Never return more than this many visible slots, even on a very large monitor — an upper safety bound so the "bounded overview" behavior doesn't disappear entirely. */
	maxVisibleCount?: number;
}

export interface OverviewCapacity {
	/** Grid columns the repack should actually use — adaptive to viewport width, capped by `columnsHint`. */
	columns: number;
	/** How many compact cards can be shown at once and still fit within `viewport` at/above the readable-zoom floor once `fitView`'s own (unclamped) fit-to-bounds math runs. */
	visibleLimit: number;
}

/**
 * Computes an adaptive VPC/AZ compact-card visible cap and column count from
 * a *real, measured* canvas viewport size (see module doc) — the direct
 * implementation of the ticket's "compute the initial camera from actual
 * rendered bounds, viewport dimensions, and a minimum readable card size."
 *
 * Derivation: `fitView` computes `zoom = min(viewport.w / bounds.w, viewport.h
 * / bounds.h) / (1+padding)`, so the largest world-space bounds that still
 * lands at/above `readableZoom` is `viewport / (readableZoom * (1+padding))`
 * per axis. Dividing that budget by one card's own footprint (plus its grid
 * gap) gives how many cards fit per axis.
 */
export function computeAdaptiveOverviewCapacity(viewport: ViewportPixelSize, options: OverviewCapacityOptions = {}): OverviewCapacity {
	const cardWidth = options.cardWidth ?? OVERVIEW_CARD_WIDTH;
	const cardHeight = options.cardHeight ?? OVERVIEW_CARD_HEIGHT;
	const gapX = options.gapX ?? OVERVIEW_GRID_GAP_X;
	const gapY = options.gapY ?? OVERVIEW_GRID_GAP_Y;
	const originX = options.originX ?? OVERVIEW_ORIGIN_X;
	const originY = options.originY ?? OVERVIEW_ORIGIN_Y;
	const columnsHint = options.columnsHint ?? OVERVIEW_GRID_COLUMNS;
	const minCardPx = options.minCardPx ?? MIN_READABLE_CARD_PX;
	const padding = options.padding ?? OVERVIEW_FIT_PADDING;
	const minVisibleCount = options.minVisibleCount ?? columnsHint;
	const maxVisibleCount = options.maxVisibleCount ?? Number.POSITIVE_INFINITY;

	if (!(viewport.width > 0) || !(viewport.height > 0)) {
		return { columns: columnsHint, visibleLimit: minVisibleCount };
	}

	const readableZoom = computeReadableZoomFloor(cardWidth, minCardPx);
	const maxWorldWidth = viewport.width / (readableZoom * (1 + padding));
	const maxWorldHeight = viewport.height / (readableZoom * (1 + padding));

	const columns = Math.max(1, Math.min(columnsHint, Math.floor((maxWorldWidth - originX + gapX) / (cardWidth + gapX))));
	const rows = Math.max(1, Math.floor((maxWorldHeight - originY + gapY) / (cardHeight + gapY)));

	const capacity = columns * rows;
	const visibleLimit = Math.min(maxVisibleCount, Math.max(minVisibleCount, capacity));
	return { columns, visibleLimit };
}
